import React, { useState } from 'react';

import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { v4 as uuid } from 'uuid';

import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import LoadingSpinner from 'components/ui/loading_spinner';
import { post } from 'helpers/api';
import { refreshActivities } from 'bundles/apex/activities/hooks';
import { showSnackbar } from 'components/ui/snackbar/helper';

import SendEmailForm from './form';
import { getFileSize, isFileReference } from './helpers';

import type { EmailFile, EmailFileReference, EmailFormState, SentEmail } from './types';

interface Props {
  initialAttachments?: EmailFileReference[];
  initialBody?: string;
  initialSubject?: string;
  path: string;
  to: string[];
  onCancel?: () => void;
  onClose: () => void; // Fires on submit and on cancel - use other callbacks to distinguish if needed
  onSend?: (sent: SentEmail) => void ;
}

interface ApiError {
  response?: { status?: number; data?: { error?: string } };
}

const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

function splitEmails(value: string): string[] {
  return value.split(',').map(email => email.trim()).filter(Boolean);
}

function errorMessage(error: ApiError): string {
  const { status, data } = error.response ?? {};
  const code = data?.error;

  if (status === 403 && code === 'reauthentication_required') {
    return 'Your email connection needs to be reconnected before you can send. ' +
      'Reconnect it from your integration settings and try again.';
  }
  if (code === 'duplicate_send') {
    return 'This email has already been sent.';
  }
  if (code === 'not_enabled') {
    return 'Sending email is not enabled for your account.';
  }

  // Validation and provider-rejection errors already carry a human-readable message.
  return code || 'Something went wrong sending your email. Please try again.';
}

function buildRequestBody(form: EmailFormState, files: EmailFile[], idempotencyKey: string): FormData | object {
  const to = splitEmails(form.to);
  const cc = splitEmails(form.cc);
  const bcc = splitEmails(form.bcc);

  if (files.length === 0) {
    return { to, cc, bcc, subject: form.subject, body: form.body, idempotencyKey };
  }

  // helpers/api passes FormData through untouched, so the keys are snake_cased here.
  const data = new FormData();
  to.forEach(email => data.append('to[]', email));
  cc.forEach(email => data.append('cc[]', email));
  bcc.forEach(email => data.append('bcc[]', email));
  data.append('subject', form.subject);
  data.append('body', form.body);
  data.append('idempotency_key', idempotencyKey);
  files.forEach((file) => {
    if (isFileReference(file)) {
      data.append('ai_attachment_gids[]', file.gid);
    } else {
      data.append('attachments[]', file);
    }
  });
  return data;
}

// Unlike most modals which are always mounted and managed with an `open` prop, this modal must be
// remounted on show as Trix reads its input once on init.
export default function SendEmailModal({
  initialAttachments, initialBody, initialSubject, path, to, onCancel, onClose, onSend
}: Props) {
  const [form, setForm] = useState<EmailFormState>({
    to: to.join(', '),
    cc: '',
    bcc: '',
    subject: initialSubject || '',
    body: initialBody || '',
  });
  const [files, setFiles] = useState<EmailFile[]>(initialAttachments || []);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [idempotencyKey] = useState(() => uuid());

  const tooLarge = files.reduce((sum, file) => sum + getFileSize(file), 0) > MAX_ATTACHMENT_BYTES;
  const canSubmit = splitEmails(form.to).length > 0 && !tooLarge;

  function handleCancel() {
    onCancel?.();
    onClose();
  }

  const updateForm = (changes: Partial<EmailFormState>) => setForm(prev => ({ ...prev, ...changes }));
  const addFiles = (added: File[]) => setFiles(prev => [...prev, ...added]);
  const removeFile = (index: number) => setFiles(prev => prev.filter((_, i) => i !== index));

  async function handleSend() {
    setError(null);
    setSending(true);

    try {
      const sentEmail: SentEmail = await post(path, buildRequestBody(form, files, idempotencyKey));

      refreshActivities();
      showSnackbar({ text: 'Email sent', variant: 'success', icon: faEnvelope });

      onSend?.(sentEmail);
      onClose();
    } catch (err) {
      setError(errorMessage(err as ApiError));
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal open onClose={handleCancel}>
      <Modal.Content size="lg">
        <Modal.Header>
          <Modal.Title className="d-flex gap-2 align-items-center">
            <FontAwesomeIcon className="text-primary" icon={faEnvelope} />
            Send Email
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && <Alert className="mb-3" severity="destructive">{error}</Alert>}
          {tooLarge && <Alert className="mb-3" severity="warning">Attachments exceed the 25 MB limit.</Alert>}

          <SendEmailForm
            files={files}
            form={form}
            showBcc={showBcc}
            showCc={showCc}
            onAddFiles={addFiles}
            onChange={updateForm}
            onRemoveFile={removeFile}
            onToggleBcc={() => setShowBcc(true)}
            onToggleCc={() => setShowCc(true)}
          />
        </Modal.Body>

        <Modal.Footer>
          <Button disabled={sending} label="Cancel" variant="ghost" onClick={handleCancel} />
          <Button disabled={sending || !canSubmit} onClick={handleSend}>
            {sending && <LoadingSpinner flex size="md" skipTopBuffer />}
            {sending ? 'Sending…' : 'Send Email'}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
